import { useState, useEffect, useRef, useCallback } from 'react';
import { useMaskedOverlay } from '../MaskedOverlay';
import { maskRrwebEvents } from './maskingUtils';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useCoViewing(session, onEnd) {
  const [connected, setConnected] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const { isMasked, isPreviewing, togglePreview } = useMaskedOverlay();

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const rrwebStopRef = useRef(null);
  const timerRef = useRef(null);
  const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  const handleEnd = useCallback(() => {
    // Backend "leave" signal — flips status: active → completed so the
    // "Your session is live!" banner stops re-firing on every sign-in.
    // Fire-and-forget: never block UI on network errors, the endpoint is
    // idempotent so duplicate calls are safe.
    try {
      const token = localStorage.getItem('auth_token');
      if (token && session?.session_id) {
        fetch(`${BACKEND_URL}/api/assisted-onboarding/sessions/${session.session_id}/leave`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,  // ensures the request flies even if the tab closes
        }).catch(() => { /* swallow — defensive Layer 2 in banner covers this */ });
      }
    } catch (e) { logger.error('leave signal failed:', e); }

    if (wsRef.current?.readyState === WebSocket.OPEN) { wsRef.current.send(JSON.stringify({ type: 'session_end' })); wsRef.current.close(); }
    if (rrwebStopRef.current) rrwebStopRef.current();
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
    if (pcRef.current) pcRef.current.close();
    if (timerRef.current) clearInterval(timerRef.current);
    onEnd?.();
  }, [onEnd, session]);

  const startDomStreaming = async (ws) => {
    try {
      const rrweb = await import('rrweb');
      rrwebStopRef.current = rrweb.record({
        emit(event) { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'rrweb_events', events: maskRrwebEvents([event], session.masked_mode) })); },
        sampling: { mousemove: true, mouseInteraction: true, scroll: 150, input: 'last' },
        maskAllInputs: session.masked_mode, maskTextSelector: session.masked_mode ? '[data-sensitive]' : undefined,
      });
    } catch (err) { logger.error('rrweb recording failed:', err); }
  };

  const setupWebRTC = async (ws) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      pc.onicecandidate = (evt) => { if (evt.candidate && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'webrtc_ice_candidate', candidate: evt.candidate })); };
      pc.ontrack = (evt) => { const audio = new Audio(); audio.srcObject = evt.streams[0]; audio.play().catch(() => {}); };
      const offer = await pc.createOffer(); await pc.setLocalDescription(offer);
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'webrtc_offer', sdp: offer }));
    } catch (err) { logger.error('WebRTC setup failed:', err); }
  };

  const startSession = useCallback(async () => {
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/co-view/${session.room_id}?role=user`);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); startDomStreaming(ws); setupWebRTC(ws); };
      ws.onmessage = (evt) => {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'peer_joined') setPeerConnected(true);
        if (msg.type === 'peer_left') setPeerConnected(false);
        if (msg.type === 'session_end') handleEnd();
        if (msg.type === 'chat_message') setChatMessages(prev => [...prev, { from: msg.from, message: msg.message, time: new Date() }]);
        if (msg.type === 'webrtc_answer' && pcRef.current) pcRef.current.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        if (msg.type === 'webrtc_ice_candidate' && pcRef.current) pcRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate));
      };
      ws.onerror = () => setConnected(false);
      ws.onclose = () => setConnected(false);
      timerRef.current = setInterval(() => setElapsedMinutes(prev => prev + 1), 60000);
    } catch (err) { logger.error('Failed to start co-viewing:', err); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const toggleMute = () => {
    if (localStreamRef.current) { localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; }); setMuted(!muted); }
  };

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'chat_message', message: chatInput }));
    setChatMessages(prev => [...prev, { from: 'user', message: chatInput, time: new Date() }]);
    setChatInput('');
  };

  useEffect(() => {
    startSession();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (rrwebStopRef.current) rrwebStopRef.current();
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
      if (pcRef.current) pcRef.current.close();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { connected, peerConnected, muted, elapsedMinutes, chatMessages, chatInput, setChatInput, showChat, setShowChat, isMasked, isPreviewing, togglePreview, toggleMute, sendChat, handleEnd };
}
